import { baseApi } from './baseApi';
import { FolderManagerItemActionNames, FolderManagerItemActionTypes, FolderManagerMimeTypes } from '../interfaces';
import { ServiceTypes } from '../constants';
import { prepareHeaders } from './helpers';
import { captureError } from '@clatter/platform';
import { addNotification } from '../store';
import { isAbortError } from '../helpers/apiErrors';
import { buildSlideKey } from '../helpers';

export const mapApiToStore = (item: SlideFromApi): FolderManagerItem<SlideFromApi> | null => {
  if (!item?.ID) {
    captureError(new Error('Could not parse search slide results: invalid slide format!'), { component: 'slides.service', skipConsole: true });
    return null;
  }

  const slideKey = buildSlideKey(item.ID);

  return {
    id: slideKey,
    displayName: item.Title || (item as any).Asset?.DisplayName || slideKey,
    type: FolderManagerMimeTypes.SLIDE,
    thumbnailUrl: item?.ThumbnailLocation,
    itemData: item,
    actions: [
      {
        name: FolderManagerItemActionNames.ADD_TO_PRESENTATION,
        type: FolderManagerItemActionTypes.MULTI,
      },
    ],
    // TODO: Remove any
  } as any;
};

interface SimilarSlideFromApi {
  id: number;
  thumbnail_location: string;
  similarity_score: number;
  similarity_text_score: number;
  similarity_image_score: number;
}

export interface SlideSimilarityInfoFromApi {
  id: number; // new slide's real id (belongs to the newly created AssetVersion)
  similarity_level: 'identity' | 'new_version' | 'new';
  similarity_score: number;
  thumbnail_location: string;
  similar_slides: SimilarSlideFromApi[];
}

export type SlideApplyAction =
  | { action: 'new'; slide_id: number }
  | { action: 'duplicate'; remove_slide_id: number; keep_slide_id: number }
  | { action: 'replace'; new_slide_id: number; old_slide_id: number };

export interface SlideApplyResult {
  new: number;
  duplicated: number;
  replaced: number;
  removed: number;
}

export type SlideSimilarityStatus = 'processing' | 'success';

export interface SlideSimilarityInfoResult {
  status: SlideSimilarityStatus;
  data: SlideSimilarityInfoFromApi[];
}

interface SlideSimilarityInfoApiResponse extends INgApiResponse<SlideSimilarityInfoFromApi[]> {
  status: SlideSimilarityStatus;
}

export const slidesService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSlideSimilarityInfo: builder.query<SlideSimilarityInfoResult, { uploadRequestId: number; assetId?: number }>({
      query: ({ uploadRequestId, assetId }) =>
        // assetId scopes matching to one specific existing asset (version-upload diffing);
        // omit it to match against the entire slide index (plain new-asset dedup detection).
        `/slides/upload-requests/${uploadRequestId}/similarity-info${assetId ? `?asset_id=${assetId}` : ''}`,
      transformResponse: (result: SlideSimilarityInfoApiResponse) => ({
        status: result?.status ?? 'processing',
        data: result?.data ?? [],
      }),
    }),
    applyUploadRequest: builder.mutation<SlideApplyResult, { uploadRequestId: number; actions: SlideApplyAction[] }>({
      query: ({ uploadRequestId, actions }) => ({
        url: `/slides/upload-requests/${uploadRequestId}/apply`,
        method: 'POST',
        body: actions,
      }),
      invalidatesTags: ['Folders', 'Assets'],
      transformResponse: (result: INgApiResponse<SlideApplyResult>) => result.data,
    }),
    cancelUploadRequest: builder.mutation<void, { uploadRequestId: number; assetVersionId?: number }>({
      query: ({ uploadRequestId, assetVersionId }) => ({
        url: `/slides/upload-requests/${uploadRequestId}/cancel`,
        method: 'POST',
        body: assetVersionId != null ? { asset_version_id: assetVersionId } : {},
      }),
      invalidatesTags: ['Folders', 'Assets'],
    }),
    getSlidesByVisual: builder.query<FolderManagerItem<SlideFromApi>[], { slideId: number; slideRepositoryId?: number }>({
      query: ({ slideId, slideRepositoryId }) => {
        const params = new URLSearchParams();
        params.set('slide_id', String(slideId));
        if (slideRepositoryId) params.set('slide_repository_id', String(slideRepositoryId));
        return { url: `/slides/visual?${params.toString()}` };
      },
      providesTags: () => [{ type: ServiceTypes.SLIDES, id: 'VISUAL' }],
      transformResponse: (result: INgApiResponse<SlideFromApi[]>): FolderManagerItem[] =>
        (result?.data || [])
          .map(mapApiToStore)
          .filter((item): item is FolderManagerItem<SlideFromApi> => item !== null),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = `Couldn't load layout suggestions. Please try again.`;
          dispatch(addNotification({ type: 'error', message }));
          captureError(error, { message, component: 'slides.service' });
        }
      },
    }),
    getSlidesBySearch: builder.query<
      FolderManagerItem<SlideFromApi>[],
      { query: string; slideRepositoryId?: number; excludeSlideId?: number }
    >({
      query: ({ query, slideRepositoryId, excludeSlideId }) => {
        const params = new URLSearchParams();
        if (query) params.set('query', query);
        if (slideRepositoryId) params.set('slide_repository_id', String(slideRepositoryId));
        if (excludeSlideId != null) {
          params.set('exclude_slide_id', String(excludeSlideId));
        }
        const qs = params.toString();
        return { url: `/slides${qs ? `?${qs}` : ''}` };
      },
      providesTags: () => [{ type: ServiceTypes.SLIDES, id: 'SEARCH' }],
      transformResponse: (result: INgApiResponse<SlideFromApi[]>): FolderManagerItem[] =>
        (result?.data || [])
          .map(mapApiToStore)
          .filter((item): item is FolderManagerItem<SlideFromApi> => item !== null),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          if (isAbortError(error)) return;
          const message = `Couldn't load slide suggestions. Please try again.`;
          dispatch(addNotification({ type: 'error', message }));
          captureError(error, { message, component: 'slides.service' });
        }
      },
    }),
    getSlideThumbnail: builder.query<
      { url: string; isGenerating: boolean },
      { url?: string | null; themeId?: string | number | null }
    >({
      queryFn: async ({ url, themeId }, api) => {
        if (!url) {
          return { error: { status: 400, data: 'URL is required' } };
        }

        let fetchUrl = url;
        if (themeId) {
          try {
            const urlObj = new URL(url);
            urlObj.searchParams.set('theme_id', String(themeId));
            fetchUrl = urlObj.toString();
          } catch {
            const separator = url.includes('?') ? '&' : '?';
            fetchUrl = `${url.replace(/([?&])theme_id=[^&]*/g, '')}${separator}theme_id=${themeId}`;
          }
        }

        const result = await fetch(fetchUrl, {
          headers: prepareHeaders(new Headers(), api),
          signal: api.signal,
        });

        if (!result.ok) {
          return { error: { status: result.status, data: await result.text() } };
        }

        const blob = await result.blob();
        const objectUrl = URL.createObjectURL(blob);

        return { data: { url: objectUrl, isGenerating: result.status === 202 } };
      },
      providesTags: (_res, _err, { url, themeId }) => [
        { type: ServiceTypes.SLIDES, id: `THUMBNAIL_${url}_${themeId ?? 'default'}` },
      ],
    }),
  }),
});

export const {
  useGetSlidesBySearchQuery,
  useGetSlideSimilarityInfoQuery,
  useGetSlidesByVisualQuery,
  useGetSlideThumbnailQuery,
  useApplyUploadRequestMutation,
  useCancelUploadRequestMutation,
} = slidesService;
